import { useState } from 'react';
import { auth, tenant as tenantApi, billing as billingApi } from '../services/api';

export default function Login({ onLogin }) {
  const [tab, setTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ nombre: '', email: '', password: '' });
  const [forgotEmail, setForgotEmail] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // ── Helper: si hay plan pendiente en localStorage, lanza el checkout ──────
  const lanzarPlanPendiente = async () => {
    const planPendiente = localStorage.getItem('devit_plan_pendiente');
    if (!planPendiente) return false;

    localStorage.removeItem('devit_plan_pendiente');
    try {
      const res = await billingApi.checkout(planPendiente);
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
        return true;
      }
    } catch {
      // Si falla el checkout, redirigimos al dashboard normalmente
    }
    return false;
  };

  // ── Login ─────────────────────────────────────────────────────────────────
  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await auth.login(loginData.email, loginData.password);
      if (res.token) {
        localStorage.setItem('devit_token', res.token);
        const me = await tenantApi.getMe();

        // Si hay plan pendiente desde el landing, lanzar checkout
        const redirigido = await lanzarPlanPendiente();
        if (!redirigido) {
          onLogin(me, res.token, 'billing');
        }
      } else {
        setError(res.detail || 'Credenciales incorrectas');
      }
    } catch {
      setError('Error de conexión');
    }
    setLoading(false);
  };

  // ── Registro ──────────────────────────────────────────────────────────────
  const handleRegister = async () => {
    if (!privacyAccepted) {
      setError('Debes aceptar la Política de Privacidad y las Condiciones de Contratación para continuar.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await auth.register(
        registerData.nombre,
        registerData.email,
        registerData.password
      );
      if (res.token) {
        localStorage.setItem('devit_token', res.token);
        const me = await tenantApi.getMe();

        // Si hay plan pendiente desde el landing, lanzar checkout
        const redirigido = await lanzarPlanPendiente();
        if (!redirigido) {
          onLogin(me, res.token);
        }
      } else {
        setError(res.detail || 'Error al registrarse');
      }
    } catch {
      setError('Error de conexión');
    }
    setLoading(false);
  };

  // ── Olvidé mi contraseña ──────────────────────────────────────────────────
  const handleForgot = async () => {
    if (!forgotEmail.trim()) {
      setError('Introduce tu email');
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      const res = await auth.resetPasswordRequest(forgotEmail.trim());
      if (res.ok || res.mensaje) {
        setSuccessMsg('Si ese email existe, recibirás un enlace para restablecer tu contraseña.');
        setForgotEmail('');
      } else {
        setError(res.detail || 'Error al enviar el email');
      }
    } catch {
      setError('Error de conexión');
    }
    setLoading(false);
  };

  return (
    <div className="login-wrap" style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <div className="login-box">
        <div className="login-logo">Devit</div>
        <div className="login-tagline">automatización inteligente · panel de control</div>

        {/* Tabs — solo login y registro tienen tab visible */}
        {tab !== 'forgot' && (
          <div className="tab-row">
            <div className={`tab ${tab === 'login' ? 'active' : ''}`} onClick={() => { setTab('login'); setError(''); setSuccessMsg(''); }}>
              Iniciar sesión
            </div>
            <div className={`tab ${tab === 'register' ? 'active' : ''}`} onClick={() => { setTab('register'); setError(''); setSuccessMsg(''); }}>
              Registrarse
            </div>
          </div>
        )}

        {/* Mensajes */}
        {error && (
          <div className="alert alert-warn" style={{ marginBottom: '16px' }}>
            <span className="alert-icon">⚠</span> {error}
          </div>
        )}
        {successMsg && (
          <div className="alert" style={{
            marginBottom: '16px', background: '#1a3a2a',
            border: '1px solid #2a5a3a', color: '#4ade80',
            borderRadius: 8, padding: '10px 14px', fontSize: 13,
          }}>
            ✓ {successMsg}
          </div>
        )}

        {/* ── Tab: Login ── */}
        {tab === 'login' && (
          <div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                className="form-input"
                type="email"
                placeholder="tu@empresa.com"
                value={loginData.email}
                onChange={e => setLoginData({ ...loginData, email: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Contraseña</label>
              <input
                className="form-input"
                type="password"
                placeholder="••••••••"
                value={loginData.password}
                onChange={e => setLoginData({ ...loginData, password: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
              />
            </div>
            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', marginTop: '8px' }}
              onClick={handleLogin}
              disabled={loading}
            >
              {loading ? 'Entrando...' : '→ Entrar al panel'}
            </button>

            {/* Olvidé mi contraseña */}
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button
                onClick={() => { setTab('forgot'); setError(''); setSuccessMsg(''); }}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--text3)', fontSize: 12,
                  textDecoration: 'underline',
                }}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </div>
        )}

        {/* ── Tab: Registro ── */}
        {tab === 'register' && (
          <div>
            <div className="form-group">
              <label className="form-label">Nombre empresa</label>
              <input
                className="form-input"
                placeholder="Mi Empresa"
                value={registerData.nombre}
                onChange={e => setRegisterData({ ...registerData, nombre: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                className="form-input"
                type="email"
                placeholder="tu@empresa.com"
                value={registerData.email}
                onChange={e => setRegisterData({ ...registerData, email: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Contraseña</label>
              <input
                className="form-input"
                type="password"
                placeholder="mín. 8 caracteres"
                value={registerData.password}
                onChange={e => setRegisterData({ ...registerData, password: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && handleRegister()}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12, marginTop: 4 }}>
              <input
                type="checkbox"
                id="privacy-check"
                checked={privacyAccepted}
                onChange={e => setPrivacyAccepted(e.target.checked)}
                style={{ marginTop: 3, accentColor: 'var(--purple, #7c4dff)', cursor: 'pointer' }}
              />
              <label htmlFor="privacy-check" style={{ fontSize: 12, color: 'var(--text2, #9b9fc4)', lineHeight: 1.5, cursor: 'pointer' }}>
                He leído y acepto la{' '}
                <a href="https://devit.es/privacidad-devit" target="_blank" rel="noreferrer" style={{ color: 'var(--purple3, #a78bfa)' }}>
                  Política de Privacidad
                </a>
                {' '}y las{' '}
                <a href="https://devit.es/terminos" target="_blank" rel="noreferrer" style={{ color: 'var(--purple3, #a78bfa)' }}>
                  Condiciones de Contratación
                </a>
              </label>
            </div>
            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', marginTop: '8px' }}
              onClick={handleRegister}
              disabled={loading || !privacyAccepted}
            >
              {loading ? 'Creando cuenta...' : '→ Crear cuenta'}
            </button>
          </div>
        )}

        {/* ── Tab: Olvidé contraseña ── */}
        {tab === 'forgot' && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <button
                onClick={() => { setTab('login'); setError(''); setSuccessMsg(''); }}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--text3)', fontSize: 12, display: 'flex',
                  alignItems: 'center', gap: 4,
                }}
              >
                ← Volver al inicio de sesión
              </button>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
                Restablecer contraseña
              </div>
              <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
                Introduce tu email y te enviaremos un enlace para crear una nueva contraseña.
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                className="form-input"
                type="email"
                placeholder="tu@empresa.com"
                value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleForgot()}
              />
            </div>
            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', marginTop: '8px' }}
              onClick={handleForgot}
              disabled={loading}
            >
              {loading ? 'Enviando...' : '→ Enviar enlace'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}